import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { Modal, Table, Tag, Button, Checkbox, Alert, message } from 'antd'
import { subscriptions } from '@services/api'
import {
    dupDate,
    dupModalCloseState,
    dupResolutionMeta,
    dupRowActionable,
    dupRowKey,
    dupRowsForDisplay,
    dupScanIssue,
    stoAmount
} from '@pages/Tranzila/utils'

// warning texts keyed by dupScanIssue (the helper itself computes the priority)
const ISSUE_TEXT = {
    stuck: 'הסריקה נתקעה — הנתונים המוצגים הם מהסריקה המוצלחת האחרונה',
    missed: 'הסריקה לא רצה במועד — הנתונים המוצגים אינם עדכניים',
    failed: 'הסריקה האחרונה נכשלה — הנתונים המוצגים הם מהסריקה המוצלחת האחרונה'
}

const TYPE_TAG = {
    duplicate: { color: 'red', label: 'כפילות' },
    // migration_pair is not harmless: if both הו"ק are active, the client will be charged twice.
    // a separate type explains the reason, but in unresolved it counts the same as duplicate.
    migration_pair: { color: 'orange', label: 'מעבר אשראי/בנק' }
}

const META_LINE = { fontSize: '0.75rem', color: '#8c8c8c' }

const Stat = ({ label, value }) => (
    <div style={{ marginLeft: '2rem' }}>
        <div style={{ fontSize: '0.75rem', color: '#8c8c8c' }}>{label}</div>
        <div style={{ fontWeight: 600 }}>{value}</div>
    </div>
)

@inject((store: any) => ({ authStore: store.authStore }))
@observer
class StoDuplicatesModal extends Component<any, any> {
    // showResolved belongs to the widget: it also loads the history. Only pagination lives here.
    state = { busy: null, page: 1 }

    // page is local to the modal, so it is reset here; showResolved is reset by its
    // owner, the widget (dupCloseState). The close icon, the mask and Esc all go through onCancel.
    handleClose = () => {
        this.setState(dupModalCloseState())
        this.props.onClose()
    }

    act = async user => {
        const { onRefresh } = this.props
        this.setState({ busy: user.user_id })
        try {
            if (user.resolved) await subscriptions.unresolveStoDuplicate(user.user_id)
            else await subscriptions.resolveStoDuplicate({ user_id: user.user_id })
            await onRefresh() // the modal stays open: the widget and counters refresh underneath it
            this.setState({ page: 1 })
        } catch (e) {
            console.log(e && e.response && e.response.data)
            message.error('הפעולה נכשלה')
        }
        this.setState({ busy: null })
    }

    columns = canEdit => [
        {
            title: 'שם הלקוח',
            key: 'name',
            align: 'right',
            render: (_, u) => (
                // same link as in the other lists (Tranzila/List): new tab for free
                <a href={`/clients/${u.user_id}`} target="_blank" rel="noopener noreferrer">
                    {u.user_name || '—'}
                </a>
            )
        },
        {
            title: 'מס׳ מזהה',
            key: 'user_id',
            width: 100,
            align: 'right',
            render: (_, u) => <span dir="ltr">{u.user_id}</span>
        },
        {
            title: 'סוג',
            key: 'type',
            width: 130,
            align: 'right',
            render: (_, u) => {
                const t = TYPE_TAG[u.type]
                return t ? <Tag color={t.color}>{t.label}</Tag> : <span>{u.type}</span>
            }
        },
        {
            title: 'הוראות קבע',
            key: 'stos',
            align: 'right',
            render: (_, u) =>
                (u.stos || []).map(s => (
                    <div key={s.sto_id} style={{ whiteSpace: 'nowrap' }}>
                        <span dir="ltr">{s.sto_id}</span>
                        {` · ${s.terminal_name} · ${stoAmount(s.charge_amount)}₪`}
                    </div>
                ))
        },
        {
            title: 'סטטוס',
            key: 'status',
            width: 190,
            align: 'right',
            render: (_, u) => {
                // meta === null only for unresolved rows: they have no resolution metadata.
                const meta = dupResolutionMeta(u)
                return meta ? (
                    <div>
                        <Tag color="green">טופל</Tag>
                        {u.historical && <Tag>היסטורי</Tag>}
                        <div style={META_LINE}>{`טופל ע"י: ${meta.by}`}</div>
                        <div style={META_LINE}>{meta.at}</div>
                        {u.note && <div style={META_LINE}>{u.note}</div>}
                    </div>
                ) : (
                    <Tag color="grey">ממתין</Tag>
                )
            }
        },
        canEdit && {
            title: 'פעולה',
            key: 'action',
            width: 130,
            align: 'right',
            render: (_, u) =>
                // A historical row describes a duplicate that may no longer exist: writing
                // the current state based on it is not allowed, so there are no actions at all.
                !dupRowActionable(u) ? null : u.resolved ? (
                    <Button size="small" loading={this.state.busy === u.user_id} onClick={() => this.act(u)}>
                        החזר לטיפול
                    </Button>
                ) : (
                    <Button
                        size="small"
                        type="primary"
                        loading={this.state.busy === u.user_id}
                        onClick={() => this.act(u)}
                    >
                        סמן כטופל
                    </Button>
                )
        }
    ]

    render() {
        const { visible, data, authStore, showResolved, onShowResolvedChange } = this.props
        const { page } = this.state
        if (!data) return null

        const roles = authStore.roles || []
        const canEdit = roles.includes('admin') || roles.includes('money')
        const issue = dupScanIssue(data)
        const users = dupRowsForDisplay(data, showResolved)

        return (
            <Modal visible={visible} onCancel={this.handleClose} footer={null} width={900} title="הוראות קבע כפולות">
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                    <Stat label="זמן הסריקה" value={dupDate(data.scanned_at)} />
                    <Stat label={'סה"כ הו"ק פעילות'} value={data.total_active_stos} />
                    <Stat label="לקוחות עם כפילות" value={data.duplicate_users_count} />
                    <Stat label="ממתינים לטיפול" value={data.unresolved_users_count} />
                </div>
                {issue && (
                    <Alert type="warning" showIcon style={{ marginBottom: '1rem' }} message={ISSUE_TEXT[issue]} />
                )}
                <Checkbox
                    checked={showResolved}
                    style={{ marginBottom: '0.8rem' }}
                    onChange={e => {
                        this.setState({ page: 1 })
                        onShowResolvedChange(e.target.checked)
                    }}
                >
                    הצג גם שטופלו
                </Checkbox>
                <Table
                    size="small"
                    bordered
                    rowKey={dupRowKey}
                    columns={this.columns(canEdit).filter(Boolean)}
                    dataSource={users}
                    pagination={{
                        current: page,
                        pageSize: 10,
                        onChange: nextPage => this.setState({ page: nextPage }),
                        hideOnSinglePage: true
                    }}
                />
            </Modal>
        )
    }
}

export default StoDuplicatesModal
